import { AloftPriceJSON } from '@aloft/shared-objects';
import {
  AloftArea,
  AloftPrice,
  Improvements,
  LatitudeLongitude,
  ManufacturedHomeFeatures,
  ManufacturedHomeFoundationDisplay,
  SubjectData,
  isDefined,
  orUndefined,
} from '@aloft/toolkit-shared';

import { makeStreetViewStaticURL } from '../../../hooks/googleMaps/makeStreetViewStaticURL.ts';
import { CompAdjustments } from '../../../redux/features/selectors/CompAdjustments.ts';
import { proximityMiles } from '../../../utils/proximityMiles.ts';
import { FeatureArea } from '../types.ts';

export const getSubjectPropertyImageUrl = (subject?: SubjectData): string | undefined =>
  subject?.readonlyData.backupListingData?.listing.Media?.[0]?.MediaURL;

export const formatFeatureArea = ({ area, unit, type, hasFeature }: FeatureArea) => {
  if (!area || !unit || !hasFeature) return '';
  const aloftArea = AloftArea.fromNumber(area, unit).toString(true);
  return type ? `${aloftArea} ${type}` : aloftArea;
};

export const formatFeatureAreas = (areas?: FeatureArea[]) => {
  if (!areas || areas.length === 0) return '';
  return areas.map(formatFeatureArea).filter(Boolean).join('; ');
};

export const getFoundationDisplay = (improvements?: Improvements): string => {
  let foundationLabel: ManufacturedHomeFoundationDisplay | undefined;
  if (improvements?.propertyType === 'manufacturedHome') {
    const { foundation } = improvements as ManufacturedHomeFeatures;
    foundationLabel = foundation as ManufacturedHomeFoundationDisplay;
  }
  return foundationLabel ? ` (${foundationLabel})` : '';
};

export const getAboveGradeBathCountAdjustment = (adjustments: CompAdjustments | undefined): AloftPrice | undefined => orUndefined(AloftPrice.schema.parse)(adjustments?.bathAboveGrade?.adjustment);

export const getBelowGradeBathCountAdjustment = (adjustments: CompAdjustments | undefined): AloftPrice | undefined => orUndefined(AloftPrice.schema.parse)(adjustments?.bathBelowGrade?.adjustment)

export const formatAdjustmentPrice = (price?: AloftPriceJSON): string =>
  price ? AloftPrice.schema.parse(price).toString(true) : '';

export const getDistanceToSubject = (
  to?: { latitude?: number; longitude?: number },
  from?: { latitude?: number; longitude?: number },
): number | undefined =>
  isDefined(to) && isDefined(from)
    ? (+(proximityMiles(to, from)?.toFixed(2) ?? 0))
    : undefined;

export const getPropertyImageWithFallback = async (
  mlsPhotoUrl?: string,
  coordinates?: LatitudeLongitude,
): Promise<string | undefined> => {
  if (mlsPhotoUrl) {
    try {
      // Try to fetch the image to check if it's accessible
      const response = await fetch(mlsPhotoUrl, {
        method: 'HEAD',
        mode: 'cors',
        signal: AbortSignal.timeout(5000), // 5 second timeout
      });

      if (response.ok) return mlsPhotoUrl;
    } catch {
      if (coordinates) return makeStreetViewStaticURL(coordinates);
    }
  }

  // If no MLS photo, try Street View
  if (coordinates) return makeStreetViewStaticURL(coordinates);
  return undefined;
};
